import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import AIChatBubble from "./components/AIChatBubble";

// Pages
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import SignupPage from "./pages/SignupPage";
import PlatformAdminLogin from "./pages/PlatformAdminLogin";

// Platform Admin Pages
import PlatformDashboard from "./pages/platform/PlatformDashboard";
import TenantsPage from "./pages/platform/TenantsPage";
import PlatformAnalytics from "./pages/platform/PlatformAnalytics";
import MessagesPage from "./pages/platform/MessagesPage";
import FinancingRequestsPage from "./pages/platform/FinancingRequestsPage";

// Client App Pages
import ClientDashboard from "./pages/client/ClientDashboard";
import CustomersPage from "./pages/client/CustomersPage";
import PropertiesPage from "./pages/client/PropertiesPage";
import EstimatesPage from "./pages/client/EstimatesPage";
import JobsPage from "./pages/client/JobsPage";
import RoutesPage from "./pages/client/RoutesPage";
import CrewsPage from "./pages/client/CrewsPage";
import InvoicesPage from "./pages/client/InvoicesPage";
import ReportsPage from "./pages/client/ReportsPage";
import SettingsPage from "./pages/client/SettingsPage";
import FinancialPage from "./pages/client/FinancialPage";
import CalendarPage from "./pages/client/CalendarPage";
import ServicePlansPage from "./pages/client/ServicePlansPage";

// Mobile Crew Page
import CrewMobilePage from "./pages/mobile/CrewMobilePage";

// Marketing Pages
import FeaturesPage from "./pages/marketing/FeaturesPage";
import PropertyManagementPage from "./pages/marketing/PropertyManagementPage";
import SchedulingPage from "./pages/marketing/SchedulingPage";
import CrewManagementPage from "./pages/marketing/CrewManagementPage";
import EstimatesInvoicingPage from "./pages/marketing/EstimatesInvoicingPage";
import ReportingPage from "./pages/marketing/ReportingPage";
import MobilePage from "./pages/marketing/MobilePage";
import PricingPage from "./pages/marketing/PricingPage";
import AboutPage from "./pages/marketing/AboutPage";
import ContactPage from "./pages/marketing/ContactPage";
import FinancingPage from "./pages/marketing/FinancingPage";
import WorkingCapitalPage from "./pages/marketing/WorkingCapitalPage";
import TermLoansPage from "./pages/marketing/TermLoansPage";
import EquipmentFinancingPage from "./pages/marketing/EquipmentFinancingPage";
import ApplyPage from "./pages/marketing/ApplyPage";
import AIAssistantPage from "./pages/marketing/AIAssistantPage";

// Customer Portal Pages
import EstimatePortalPage from "./pages/portal/EstimatePortalPage";

// Protected Route Component
const ProtectedRoute = ({ children, requirePlatformAdmin = false }) => {
  const { user, loading, isPlatformAdmin } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (requirePlatformAdmin && !isPlatformAdmin()) {
    return <Navigate to="/app" replace />;
  }
  
  if (!requirePlatformAdmin && isPlatformAdmin()) {
    return <Navigate to="/platform" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/platform/login" element={<PlatformAdminLogin />} />
      
      {/* Marketing Pages */}
      <Route path="/features" element={<FeaturesPage />} />
      <Route path="/features/property-management" element={<PropertyManagementPage />} />
      <Route path="/features/scheduling" element={<SchedulingPage />} />
      <Route path="/features/crew-management" element={<CrewManagementPage />} />
      <Route path="/features/estimates-invoicing" element={<EstimatesInvoicingPage />} />
      <Route path="/features/reporting" element={<ReportingPage />} />
      <Route path="/features/mobile" element={<MobilePage />} />
      <Route path="/features/ai-assistant" element={<AIAssistantPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/contact" element={<ContactPage />} />
      
      {/* Business Financing Pages */}
      <Route path="/financing" element={<FinancingPage />} />
      <Route path="/financing/working-capital" element={<WorkingCapitalPage />} />
      <Route path="/financing/term-loans" element={<TermLoansPage />} />
      <Route path="/financing/equipment" element={<EquipmentFinancingPage />} />
      <Route path="/apply" element={<ApplyPage />} />
      
      {/* Customer Portal (Public) */}
      <Route path="/portal/estimate/:token" element={<EstimatePortalPage />} />
      
      {/* Platform Admin Routes */}
      <Route path="/platform" element={
        <ProtectedRoute requirePlatformAdmin>
          <PlatformDashboard />
        </ProtectedRoute>
      } />
      <Route path="/platform/tenants" element={
        <ProtectedRoute requirePlatformAdmin>
          <TenantsPage />
        </ProtectedRoute>
      } />
      <Route path="/platform/analytics" element={
        <ProtectedRoute requirePlatformAdmin>
          <PlatformAnalytics />
        </ProtectedRoute>
      } />
      <Route path="/platform/messages" element={
        <ProtectedRoute requirePlatformAdmin>
          <MessagesPage />
        </ProtectedRoute>
      } />
      <Route path="/platform/financing-requests" element={
        <ProtectedRoute requirePlatformAdmin>
          <FinancingRequestsPage />
        </ProtectedRoute>
      } />
      
      {/* Client App Routes */}
      <Route path="/app" element={
        <ProtectedRoute>
          <ClientDashboard />
        </ProtectedRoute>
      } />
      <Route path="/app/customers" element={
        <ProtectedRoute>
          <CustomersPage />
        </ProtectedRoute>
      } />
      <Route path="/app/properties" element={
        <ProtectedRoute>
          <PropertiesPage />
        </ProtectedRoute>
      } />
      <Route path="/app/estimates" element={
        <ProtectedRoute>
          <EstimatesPage />
        </ProtectedRoute>
      } />
      <Route path="/app/jobs" element={
        <ProtectedRoute>
          <JobsPage />
        </ProtectedRoute>
      } />
      <Route path="/app/calendar" element={
        <ProtectedRoute>
          <CalendarPage />
        </ProtectedRoute>
      } />
      <Route path="/app/routes" element={
        <ProtectedRoute>
          <RoutesPage />
        </ProtectedRoute>
      } />
      <Route path="/app/crews" element={
        <ProtectedRoute>
          <CrewsPage />
        </ProtectedRoute>
      } />
      <Route path="/app/invoices" element={
        <ProtectedRoute>
          <InvoicesPage />
        </ProtectedRoute>
      } />
      <Route path="/app/reports" element={
        <ProtectedRoute>
          <ReportsPage />
        </ProtectedRoute>
      } />
      <Route path="/app/settings" element={
        <ProtectedRoute>
          <SettingsPage />
        </ProtectedRoute>
      } />
      <Route path="/app/financial" element={
        <ProtectedRoute>
          <FinancialPage />
        </ProtectedRoute>
      } />
      <Route path="/app/service-plans" element={
        <ProtectedRoute>
          <ServicePlansPage />
        </ProtectedRoute>
      } />
      
      {/* Mobile Crew Route */}
      <Route path="/crew" element={
        <ProtectedRoute>
          <CrewMobilePage />
        </ProtectedRoute>
      } />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <AIChatBubble />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
